// components/ExcelUploader.js
import React, { useState, useRef } from 'react';
import { Upload, Download, FileText, AlertCircle, CheckCircle, X } from 'lucide-react';
import { parseExcelFile, validateProductData, convertToProductFormat, generateSampleCSV } from '../utils/excelUtils';

const ExcelUploader = ({ onProductsImported, onClose }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [errors, setErrors] = useState([]);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setErrors(['CSV 또는 Excel 파일만 업로드할 수 있습니다.']);
      return;
    }

    setUploading(true);
    setErrors([]);

    try {
      const data = await parseExcelFile(file);
      const convertedData = convertToProductFormat(data);
      const validationErrors = validateProductData(convertedData);

      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        setPreviewData(null);
      } else {
        setPreviewData(convertedData);
      }
    } catch (error) {
      setErrors([`파일 처리 중 오류가 발생했습니다: ${error.message}`]);
      setPreviewData(null);
    }

    setUploading(false);
  };

  const handleImport = () => {
    if (previewData && previewData.length > 0) {
      onProductsImported(previewData);
      onClose();
    }
  };

  const onButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">품목 일괄 업로드</h2>
            <p className="text-sm text-gray-500 mt-1">CSV 또는 Excel 파일로 품목을 일괄 등록합니다</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* 템플릿 다운로드 */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-900 mb-2">1. 템플릿 다운로드</h3>
            <button
              onClick={generateSampleCSV}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md text-sm flex items-center gap-2 transition-colors"
            >
              <Download className="w-4 h-4" />
              템플릿 CSV 다운로드
            </button>
            <p className="text-sm text-gray-500 my-2">
              필수 컬럼:  category(카테고리), name(품목/서비스명), description(내용), unitPrice(단가), unit(단위)
            </p>
          </div>

          {/* 파일 업로드 */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-900 mb-2">2. 파일 업로드</h3>
            <div
              className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive 
                  ? 'border-blue-400 bg-blue-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              
              {uploading ? (
                <div className="flex flex-col items-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                  <p className="text-sm text-gray-600">파일을 처리하는 중...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <Upload className="w-12 h-12 text-gray-400 mb-4" />
                  <p className="text-lg font-medium text-gray-900 mb-2">
                    파일을 여기에 드래그하거나 클릭해서 선택하세요
                  </p>
                  <p className="text-sm text-gray-500 mb-4">CSV, XLSX, XLS 파일을 지원합니다</p>
                  <button
                    onClick={onButtonClick}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm transition-colors"
                  >
                    파일 선택
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 오류 표시 */}
          {errors.length > 0 && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center mb-2">
                <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                <h4 className="text-sm font-medium text-red-800">오류가 발생했습니다</h4>
              </div>
              <ul className="text-sm text-red-700 space-y-1">
                {errors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* 미리보기 */}
          {previewData && previewData.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center mb-4">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                <h3 className="text-sm font-medium text-gray-900">
                  미리보기 ({previewData.length}개 품목)
                </h3>
              </div>
              
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-64">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          품목명
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          카테고리
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          단가
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          단위
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          설명
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {previewData.slice(0, 10).map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm text-gray-900">{item.name}</td>
                          <td className="px-4 py-2 text-sm text-gray-500">{item.category}</td>
                          <td className="px-4 py-2 text-sm text-gray-500">
                            {item.unitPrice.toLocaleString()}원
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500">{item.unit}</td>
                          <td className="px-4 py-2 text-sm text-gray-500">{item.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {previewData.length > 10 && (
                  <div className="px-4 py-2 bg-gray-50 text-xs text-gray-500 text-center">
                    ... 및 {previewData.length - 10}개 더
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-500">
            <FileText className="w-4 h-4 inline mr-1" />
            지원 형식: CSV, XLSX, XLS
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
            >
              취소
            </button>
            <button
              onClick={handleImport}
              disabled={!previewData || previewData.length === 0}
              className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-2 rounded-md transition-colors"
            >
              {previewData ? `${previewData.length}개 품목 가져오기` : '가져오기'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExcelUploader;